
const PublishedArticles = () => {
  const articles = [
    {
      title: "Advanced Matrix Acidizing Techniques for Carbonate Reservoirs",
      journal: "SPE Journal of Petroleum Technology",
      year: "2024",
      authors: "Dr. Ahmed Al-Rashid, Dr. Sarah Mitchell",
      abstract: "Comprehensive study on novel acid systems for enhanced carbonate stimulation, achieving 250% productivity improvement in UAE fields.",
      keyFindings: [
        "Optimized acid formulation for high-temperature conditions",
        "Reduced formation damage by 80%",
        "Extended acid penetration depth by 300%"
      ],
      downloadLink: "#",
      image: "https://public.readdy.ai/ai/img_res/f5e40ab38c3e4424a32d5ef95ee7c697.jpg"
    },
    {
      title: "Innovative Diverting Agents for Heterogeneous Formations",
      journal: "International Journal of Oil & Gas Engineering",
      year: "2024",
      authors: "Dr. Hassan Al-Mahmoud, Dr. Maria Rodriguez",
      abstract: "Development of smart diverting agents that respond to formation heterogeneity, ensuring uniform acid distribution across pay zones.",
      keyFindings: [
        "Self-activating diversion mechanism",
        "95% uniform acid coverage achieved",
        "Applicable to complex reservoir geometries"
      ],
      downloadLink: "#",
      image: "https://readdy.ai/api/search-image?query=Diverting%20agents%20research%20with%20Golden%20Black%20logo%2C%20innovative%20chemical%20formulation%20for%20heterogeneous%20formations%20with%20laboratory%20testing%20equipment&width=400&height=250&seq=diverting-article&orientation=landscape"
    },
    {
      title: "Environmentally Sustainable Acid Stimulation Technologies",
      journal: "Journal of Sustainable Energy",
      year: "2023",
      authors: "Dr. Maria Rodriguez, Dr. Ahmed Al-Rashid",
      abstract: "Green chemistry approach to acid stimulation, developing biodegradable acid systems without compromising performance.",
      keyFindings: [
        "100% biodegradable acid additives",
        "Maintained stimulation effectiveness",
        "Reduced environmental impact by 90%"
      ],
      downloadLink: "#",
      image: "https://readdy.ai/api/search-image?query=Sustainable%20acid%20stimulation%20research%20with%20Golden%20Black%20branding%2C%20green%20chemistry%20laboratory%20developing%20environmentally%20friendly%20stimulation%20technologies&width=400&height=250&seq=sustainable-article&orientation=landscape"
    },
    {
      title: "Real-Time Monitoring of Acid Stimulation Treatments",
      journal: "SPE Production & Operations",
      year: "2023",
      authors: "Dr. Sarah Mitchell, Dr. Hassan Al-Mahmoud",
      abstract: "Integration of IoT sensors and AI analytics for real-time optimization of acid stimulation treatments in offshore operations.",
      keyFindings: [
        "Real-time treatment optimization",
        "40% improvement in treatment efficiency",
        "Predictive analytics for treatment success"
      ],
      downloadLink: "#",
      image: "https://readdy.ai/api/search-image?query=Real-time%20monitoring%20technology%20with%20Golden%20Black%20logo%2C%20IoT%20sensors%20and%20AI%20analytics%20for%20acid%20stimulation%20treatment%20optimization&width=400&height=250&seq=monitoring-article&orientation=landscape"
    },
    {
      title: "High-Temperature Acid Systems for Deep Wells",
      journal: "Petroleum Science and Technology",
      year: "2023",
      authors: "Dr. Ahmed Al-Rashid, Dr. Maria Rodriguez",
      abstract: "Development of thermally stable acid systems for extreme downhole conditions, tested in wells exceeding 200°C.",
      keyFindings: [
        "Stable performance up to 250°C",
        "Extended reaction control at high temperatures",
        "Successful field trials in deep UAE wells"
      ],
      downloadLink: "#",
      image: "https://readdy.ai/api/search-image?query=High-temperature%20acid%20systems%20with%20Golden%20Black%20branding%2C%20extreme%20conditions%20testing%20laboratory%20for%20deep%20well%20applications&width=400&height=250&seq=hightemp-article&orientation=landscape"
    },
    {
      title: "Corrosion Inhibition in Acid Stimulation Operations",
      journal: "Corrosion Engineering Journal",
      year: "2022",
      authors: "Dr. Hassan Al-Mahmoud, Dr. Sarah Mitchell",
      abstract: "Novel corrosion inhibitor formulations for acid stimulation, providing superior protection for downhole equipment.",
      keyFindings: [
        "99% corrosion inhibition efficiency",
        "Compatible with all acid systems",
        "Extended equipment protection duration"
      ],
      downloadLink: "#",
      image: "https://readdy.ai/api/search-image?query=Corrosion%20inhibition%20research%20with%20Golden%20Black%20logo%2C%20advanced%20laboratory%20testing%20for%20acid%20stimulation%20equipment%20protection&width=400&height=250&seq=corrosion-article&orientation=landscape"
    }
  ];

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Published Research & Articles
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Golden Black's commitment to innovation is reflected in our extensive research publications, 
            contributing to the advancement of acid stimulation and well intervention technologies.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {articles.map((article, index) => (
            <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
              <img 
                src={article.image}
                alt={article.title}
                className="w-full h-48 object-cover"
              />
              
              <div className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm text-teal-600 font-semibold">{article.journal}</span>
                  <span className="text-sm text-gray-500">{article.year}</span>
                </div>
                
                <h3 className="text-lg font-bold text-gray-900 mb-3 leading-tight">{article.title}</h3>
                <p className="text-sm text-gray-600 mb-3">By: {article.authors}</p>
                <p className="text-gray-600 text-sm mb-4 leading-relaxed">{article.abstract}</p>
                
                <div className="mb-4">
                  <h4 className="font-semibold text-gray-900 mb-2 text-sm">Key Findings:</h4>
                  <div className="space-y-1">
                    {article.keyFindings.map((finding, findingIndex) => (
                      <div key={findingIndex} className="flex items-start">
                        <i className="ri-check-line text-teal-600 mr-2 text-sm mt-0.5"></i>
                        <span className="text-gray-700 text-xs">{finding}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="flex justify-between items-center">
                  <a 
                    href={article.downloadLink}
                    className="text-teal-600 hover:text-teal-700 text-sm font-medium flex items-center cursor-pointer"
                  >
                    <i className="ri-download-line mr-1"></i>
                    Download PDF
                  </a>
                  <div className="flex items-center text-gray-500 text-xs">
                    <i className="ri-eye-line mr-1"></i>
                    View Abstract
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-16 bg-white rounded-xl shadow-lg p-8">
          <div className="text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Research Collaboration
            </h3>
            <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
              Golden Black actively collaborates with leading universities and research institutions 
              to advance the science of chemical solutions for the oil and gas industry.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-book-open-line text-2xl text-teal-600"></i>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">25+ Publications</h4>
                <p className="text-gray-600 text-sm">Peer-reviewed research articles</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-award-line text-2xl text-teal-600"></i>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">5 Industry Awards</h4>
                <p className="text-gray-600 text-sm">Recognition for innovation excellence</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-team-line text-2xl text-teal-600"></i>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">10+ Partnerships</h4>
                <p className="text-gray-600 text-sm">Academic and industry collaborations</p>
              </div>
            </div>
            
            <a 
              href="/contact" 
              className="bg-teal-600 text-white px-8 py-3 rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer inline-block"
            >
              Explore Research Partnerships
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PublishedArticles;
